import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMenuLayoutContext } from '../../../contexts/MenuLayoutContext';
import { useCuisinesContext } from '../../../contexts/CuisinesContext';
import {
  CreateRestaurantResponseInterface,
  RestaurantDetailsInterface,
  RestaurantImagesInterface
} from '../../../types/RestaurantInterface';
import {
  assignPackageToRestaurant,
  createRestaurant,
  editRestaurant,
  uploadRestaurantImages
} from '../../../api/restaurant';
import { updateMenuLayout } from '../../../api/menuLayouts';
import { openModal, useModalContext } from '../../../contexts/ModalContext';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import { PAGE_MENUS, PAGE_SETTINGS } from '../../../constants/UriConstants';
import { displayLoading, hideLoading, useAsyncContext } from '../../../contexts/AsyncContext';
import { useCheckoutContext } from '../../../contexts/CheckoutContext';
import { buildRestaurantHours } from '../../../utils/time';
import { RestaurantWizardSchema, RestaurantWizardFormValues } from './restaurantWizardSchema';
import { RestaurantWizardStep, RestaurantWizardSteps } from '../../../constants/RestaurantWizardConstants';
import {
  uploadGalleryMedia,
  uploadProfileMedia,
  buildEditRestaurantRequest,
  buildCreateRestaurantRequest,
  getSubmitErrorMessage,
  extractNewImageBlobs,
  extractNewMediaBlobs,
  getImageIdsToDelete,
  buildMergedImages
} from './restaurantWizardUtils';
import { RestaurantImages } from './restaurantWizardTypes';

const toBlob = (image: Blob | string | null): Blob | null => (image instanceof Blob ? image : null);

const useRestaurantWizardSubmit = (
  isEdit: boolean,
  isSuper: boolean,
  restaurantImages: RestaurantImages,
  goToStep: (_step: RestaurantWizardStep) => void
) => {
  const navigate = useNavigate();
  const { dispatch } = useModalContext();
  const { dispatch: asyncDispatch } = useAsyncContext();
  const { currentRestaurant, addRestaurant, updateRestaurant } = useRestaurantContext();
  const { managerPackageID, removeSession } = useCheckoutContext();
  const { getMenuLayout } = useMenuLayoutContext();
  const { getCuisine } = useCuisinesContext();
  const { logoImage, thumbnailImage, profileImages, menuCoverImage, galleryImages } = restaurantImages;

  const handleEditSubmit = async (value: RestaurantWizardFormValues, phoneNumber: string) => {
    if (!thumbnailImage || !profileImages?.length || !menuCoverImage) {
      openModal({
        dispatch,
        title: 'Required Image is missing.',
        message: 'A required image is not provided. Check your input and try again.',
        onDismiss: () => goToStep(RestaurantWizardSteps.PHOTOS)
      });
      return;
    }

    await editRestaurant(buildEditRestaurantRequest(value, currentRestaurant, phoneNumber));

    if (value.layoutID !== currentRestaurant?.menuLayout?.layoutID) {
      await updateMenuLayout(value.layoutID);
    }

    const logo = toBlob(logoImage);
    const thumbnail = toBlob(thumbnailImage);
    const menuCover = toBlob(menuCoverImage);
    const imagesToDelete = getImageIdsToDelete(currentRestaurant?.images, logo, thumbnail, menuCover);

    const {
      newGalleryImagesToDelete: galleryImagesToDelete,
      newGalleryOrder: galleryOrder,
      newGalleryImages: galleryImagesToUpload
    } = await uploadGalleryMedia(currentRestaurant?.images?.albums?.[0]?.media, galleryImages ?? []);

    const {
      newProfileImagesToDelete: profileImagesToDelete,
      newProfileOrder: profileOrder,
      newProfileImages: profileImagesToUpload
    } = await uploadProfileMedia(currentRestaurant?.images?.profile ?? [], profileImages ?? []);

    const hasImageChanges =
      logo ||
      thumbnail ||
      menuCover ||
      profileImagesToUpload?.length ||
      galleryImagesToUpload?.length ||
      imagesToDelete?.length ||
      galleryImagesToDelete?.length ||
      profileImagesToDelete?.length ||
      galleryOrder?.length ||
      profileOrder?.length;

    const uploadedImages: RestaurantImagesInterface = hasImageChanges
      ? await uploadRestaurantImages(
          logo,
          thumbnail,
          profileImagesToUpload?.length ? profileImagesToUpload : null,
          menuCover,
          galleryImagesToUpload,
          galleryImagesToDelete,
          profileImagesToDelete,
          imagesToDelete,
          galleryOrder,
          profileOrder
        )
      : null;

    updateRestaurant({
      ...currentRestaurant,
      name: value.name,
      description: value.description,
      primaryTagline: value.primaryTagline || undefined,
      secondaryTagline: value.secondaryTagline || undefined,
      phone: phoneNumber,
      email: value.email,
      website: value.website,
      address: {
        ...currentRestaurant?.address,
        address1: value.address?.address1,
        address2: value.address?.address2,
        city: value.address?.city,
        governingDistrict: value.address?.governingDistrict,
        postalCode: value.address?.postCode
      },
      menuLayout: getMenuLayout(value.layoutID),
      cuisine: getCuisine(value.cuisine),
      socials: value.socials,
      restaurantHours: buildRestaurantHours(value?.restaurantHours),
      availabilityNotes: value?.availabilityNotes,
      images: buildMergedImages(
        currentRestaurant?.images,
        uploadedImages,
        imagesToDelete,
        galleryImagesToDelete,
        profileImagesToDelete,
        galleryOrder,
        profileOrder
      )
    });

    toast.success('Successfully Updated Restaurant!', { position: toast.POSITION.TOP_RIGHT });
    navigate(`/${PAGE_SETTINGS}`);
  };

  const handleCreateSubmit = async (value: RestaurantWizardFormValues, phoneNumber: string) => {
    const createdRestaurant: CreateRestaurantResponseInterface = await createRestaurant(
      buildCreateRestaurantRequest(value, phoneNumber)
    );

    const newGalleryBlobs = extractNewImageBlobs(galleryImages ?? []);
    const newProfileBlobs = extractNewMediaBlobs(profileImages ?? []);

    const images: RestaurantImagesInterface = await uploadRestaurantImages(
      toBlob(logoImage),
      toBlob(thumbnailImage),
      newProfileBlobs.length ? newProfileBlobs : null,
      toBlob(menuCoverImage),
      newGalleryBlobs.length ? newGalleryBlobs : null,
      [],
      [],
      [],
      [],
      [],
      createdRestaurant.restaurantID
    );

    await updateMenuLayout(value.layoutID, createdRestaurant.restaurantID);

    addRestaurant({
      ...createdRestaurant,
      menuLayout: getMenuLayout(value.layoutID),
      images
    } as RestaurantDetailsInterface);

    if (!isSuper) {
      await assignPackageToRestaurant(managerPackageID, createdRestaurant.restaurantID);
      removeSession();
    }

    toast.success('Successfully Created Restaurant!', { position: toast.POSITION.TOP_RIGHT });
    navigate(`/${createdRestaurant.restaurantID}/${PAGE_MENUS}`);
  };

  return async (rawValue: RestaurantWizardFormValues) => {
    if (!RestaurantWizardSchema.isValidSync(rawValue)) return;
    const value = RestaurantWizardSchema.cast(rawValue);
    const phoneNumber = value.phone.slice(2);

    displayLoading({ dispatch: asyncDispatch, message: `${isEdit ? 'Updating' : 'Creating'} Restaurant...` });

    try {
      if (isEdit) {
        await handleEditSubmit(value, phoneNumber);
      } else {
        await handleCreateSubmit(value, phoneNumber);
      }
    } catch (error) {
      openModal({
        dispatch,
        title: `Error occurred while ${isEdit ? 'editing' : 'creating'} restaurant.`,
        message: getSubmitErrorMessage(error)
      });
    } finally {
      hideLoading(asyncDispatch);
    }
  };
};

export default useRestaurantWizardSubmit;
